document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.stats-charts').forEach((section) => {
    const dropdown = section.querySelector('.stats-charts__class');
    const dropdownToggle = section.querySelector('.stats-charts__class-droplist-checked');
    const dropdownLabel = section.querySelector('.stats-charts__class-current');
    const dropdownPopup = section.querySelector('.stats-charts__class-popup');
    const dropdownOptions = Array.from(
      section.querySelectorAll('.stats-charts__class-popup-item')
    );
    const subtitle = section.querySelector('.stats-charts__subtitle');
    const results = section.querySelector('.stats-charts__results');
    const resultSets = Array.from(section.querySelectorAll('.stats-charts__chart-container'));

    if (
      !dropdown ||
      !dropdownToggle ||
      !dropdownLabel ||
      !dropdownPopup ||
      dropdownOptions.length === 0 ||
      !subtitle ||
      !results ||
      resultSets.length === 0
    ) {
      return;
    }

    let currentClassValue = null;
    let popupHideTimer = null;
    let revealFrame = null;
    let revealInnerFrame = null;
    let stabilizationFrame = null;

    const isMotionDisabled = () =>
      window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
      document.body.dataset.accessibility === 'true' ||
      document.body.dataset.noImages === 'true';

    const closeDropdown = ({ restoreFocus = false } = {}) => {
      window.clearTimeout(popupHideTimer);
      dropdown.classList.remove('is-open');
      dropdownToggle.setAttribute('aria-expanded', 'false');

      if (restoreFocus) {
        dropdownToggle.focus();
      }

      if (isMotionDisabled()) {
        dropdownPopup.hidden = true;
        return;
      }

      popupHideTimer = window.setTimeout(() => {
        dropdownPopup.hidden = true;
      }, 180);
    };

    const openDropdown = ({ focusSelected = false } = {}) => {
      window.clearTimeout(popupHideTimer);
      dropdownPopup.hidden = false;
      dropdownPopup.getBoundingClientRect();
      dropdown.classList.add('is-open');
      dropdownToggle.setAttribute('aria-expanded', 'true');

      if (focusSelected) {
        const selectedOption =
          dropdownOptions.find((option) => option.getAttribute('aria-selected') === 'true') ||
          dropdownOptions[0];

        window.requestAnimationFrame(() => {
          selectedOption.focus();
        });
      }
    };

    const scheduleStabilization = () => {
      if (stabilizationFrame !== null) {
        window.cancelAnimationFrame(stabilizationFrame);
      }

      stabilizationFrame = window.requestAnimationFrame(() => {
        stabilizationFrame = null;

        const currentDescription = subtitle.textContent;
        const descriptions = [
          ...new Set(resultSets.map((resultSet) => resultSet.dataset.resultDescription)),
        ];

        const resultsWidth = results.getBoundingClientRect().width;
        const subtitleWidth = subtitle.getBoundingClientRect().width;
        const descriptionProbe = subtitle.cloneNode(false);

        descriptionProbe.removeAttribute('id');
        descriptionProbe.setAttribute('aria-hidden', 'true');
        descriptionProbe.style.position = 'fixed';
        descriptionProbe.style.top = '0';
        descriptionProbe.style.left = '0';
        descriptionProbe.style.zIndex = '-1';
        descriptionProbe.style.width = `${subtitleWidth}px`;
        descriptionProbe.style.maxWidth = 'none';
        descriptionProbe.style.minHeight = '0';
        descriptionProbe.style.height = 'auto';
        descriptionProbe.style.visibility = 'hidden';
        descriptionProbe.style.pointerEvents = 'none';
        section.append(descriptionProbe);

        const maxDescriptionHeight = descriptions.reduce((maxHeight, description) => {
          descriptionProbe.textContent = description;
          return Math.max(maxHeight, descriptionProbe.getBoundingClientRect().height);
        }, 0);

        descriptionProbe.remove();
        subtitle.textContent = currentDescription;

        const measureResultSet = (resultSet, stableCardHeight, measure) => {
          const resultSetProbe = resultSet.cloneNode(true);

          resultSetProbe.hidden = false;
          resultSetProbe.setAttribute('aria-hidden', 'true');
          resultSetProbe.style.position = 'fixed';
          resultSetProbe.style.top = '0';
          resultSetProbe.style.left = '0';
          resultSetProbe.style.zIndex = '-1';
          resultSetProbe.style.width = `${resultsWidth}px`;
          resultSetProbe.style.visibility = 'hidden';
          resultSetProbe.style.pointerEvents = 'none';
          resultSetProbe.style.setProperty(
            '--stats-charts-stable-card-min-height',
            `${stableCardHeight}px`
          );
          section.append(resultSetProbe);

          const measurement = measure(resultSetProbe);

          resultSetProbe.remove();

          return measurement;
        };

        resultSets.forEach((resultSet) => {
          const shouldStack = measureResultSet(
            resultSet,
            0,
            (measuredResultSet) => {
              measuredResultSet.classList.remove('is-stacked');

              const columnCount = getComputedStyle(measuredResultSet)
                .gridTemplateColumns
                .split(' ').length;

              if (columnCount !== 3) {
                return false;
              }

              return Array.from(
                measuredResultSet.querySelectorAll('.stats-charts__chart-title')
              ).some((title) => {
                const lineHeight = Number.parseFloat(getComputedStyle(title).lineHeight);

                return title.getBoundingClientRect().height > lineHeight + 0.5;
              });
            }
          );

          resultSet.classList.toggle('is-stacked', shouldStack);
        });

        const maxCardHeight = resultSets.reduce(
          (currentMaxHeight, resultSet) =>
            Math.max(
              currentMaxHeight,
              measureResultSet(resultSet, 0, (measuredResultSet) =>
                Math.max(
                  ...Array.from(
                    measuredResultSet.querySelectorAll('.stats-charts__chart'),
                    (card) => card.getBoundingClientRect().height
                  )
                )
              )
            ),
          0
        );

        const stableCardHeight = Math.ceil(maxCardHeight);

        const activeResultSet =
          resultSets.find((resultSet) => !resultSet.hidden) || resultSets[0];
        const activeResultsHeight = measureResultSet(
          activeResultSet,
          stableCardHeight,
          (measuredResultSet) => measuredResultSet.getBoundingClientRect().height
        );

        section.style.setProperty(
          '--stats-charts-description-min-height',
          `${Math.ceil(maxDescriptionHeight)}px`
        );
        section.style.setProperty(
          '--stats-charts-stable-card-min-height',
          `${stableCardHeight}px`
        );
        section.style.setProperty(
          '--stats-charts-results-height',
          `${Math.ceil(activeResultsHeight)}px`
        );
      });
    };

    const applyOptionValues = (resultSet, option) => {
      let optionValues;

      try {
        optionValues = JSON.parse(option.dataset.results);
      } catch (error) {
        return;
      }

      const bars = Array.from(resultSet.querySelectorAll('.stats-charts__chart-bar'));

      if (!Array.isArray(optionValues) || optionValues.length !== bars.length) {
        return;
      }

      bars.forEach((bar, index) => {
        const optionValue = optionValues[index];
        const desktopFill = optionValue.desktopFill || optionValue.fill;
        const tabletFill = optionValue.tabletFill || optionValue.fill;
        const mobileFill = optionValue.mobileFill || optionValue.fill;
        const value = optionValue.value;
        const valueElement = bar.querySelector('.stats-charts__chart-value');

        if (!valueElement || !desktopFill || !tabletFill || !mobileFill || !value) {
          return;
        }

        bar.style.setProperty('--stats-chart-fill-desktop', desktopFill);
        bar.style.setProperty('--stats-chart-fill-tablet', tabletFill);
        bar.style.setProperty('--stats-chart-fill-mobile', mobileFill);
        bar.setAttribute('aria-label', `${bar.dataset.label}: ${value}`);
        valueElement.textContent = value;
      });
    };

    const activateOption = (option, { animate = true } = {}) => {
      const resultSetKey = option.dataset.resultSet;
      const classValue = option.dataset.value;
      const activeResultSet = resultSets.find(
        (resultSet) => resultSet.dataset.resultSet === resultSetKey
      );

      if (!activeResultSet) {
        return;
      }

      dropdownOptions.forEach((dropdownOption) => {
        dropdownOption.setAttribute(
          'aria-selected',
          String(dropdownOption === option)
        );
      });

      dropdownLabel.textContent = option.textContent.trim();
      dropdownToggle.setAttribute(
        'aria-label',
        `${dropdownToggle.dataset.label}: ${option.textContent.trim()}`
      );
      dropdownToggle.setAttribute('aria-activedescendant', option.id);

      if (currentClassValue === classValue) {
        return;
      }

      if (revealFrame !== null) {
        window.cancelAnimationFrame(revealFrame);
      }
      if (revealInnerFrame !== null) {
        window.cancelAnimationFrame(revealInnerFrame);
      }

      results.classList.remove('is-visible');
      applyOptionValues(activeResultSet, option);
      resultSets.forEach((resultSet) => {
        resultSet.hidden = resultSet !== activeResultSet;
      });

      subtitle.textContent = activeResultSet.dataset.resultDescription;
      currentClassValue = classValue;
      scheduleStabilization();

      if (animate) {
        revealFrame = window.requestAnimationFrame(() => {
          revealFrame = null;
          revealInnerFrame = window.requestAnimationFrame(() => {
            revealInnerFrame = null;
            results.classList.add('is-visible');
          });
        });
      }
    };

    const selectOption = (option) => {
      activateOption(option);
      closeDropdown({ restoreFocus: true });
    };

    dropdownToggle.addEventListener('click', () => {
      if (dropdown.classList.contains('is-open')) {
        closeDropdown();
      } else {
        openDropdown();
      }
    });

    dropdownToggle.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        openDropdown({ focusSelected: true });
      } else if (event.key === 'Escape') {
        closeDropdown();
      }
    });

    dropdownOptions.forEach((option) => {
      option.addEventListener('click', () => {
        selectOption(option);
      });

      option.addEventListener('keydown', (event) => {
        const currentIndex = dropdownOptions.indexOf(option);
        let nextIndex = null;

        if (event.key === 'ArrowDown') {
          nextIndex = (currentIndex + 1) % dropdownOptions.length;
        } else if (event.key === 'ArrowUp') {
          nextIndex =
            (currentIndex - 1 + dropdownOptions.length) % dropdownOptions.length;
        } else if (event.key === 'Home') {
          nextIndex = 0;
        } else if (event.key === 'End') {
          nextIndex = dropdownOptions.length - 1;
        } else if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          selectOption(option);
          return;
        } else if (event.key === 'Escape') {
          event.preventDefault();
          closeDropdown({ restoreFocus: true });
          return;
        } else if (event.key === 'Tab') {
          closeDropdown();
          return;
        }

        if (nextIndex !== null) {
          event.preventDefault();
          dropdownOptions[nextIndex].focus();
        }
      });
    });

    document.addEventListener('click', (event) => {
      if (!dropdown.contains(event.target)) {
        closeDropdown();
      }
    });

    window.addEventListener('resize', scheduleStabilization);

    const accessibilityObserver = new MutationObserver(scheduleStabilization);
    accessibilityObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['data-font-size', 'data-no-images', 'data-accessibility'],
    });

    const initiallySelectedOption =
      dropdownOptions.find((option) => option.getAttribute('aria-selected') === 'true') ||
      dropdownOptions[0];

    activateOption(initiallySelectedOption, { animate: false });
    scheduleStabilization();

    document.fonts?.ready.then(scheduleStabilization);
  });
});
